<template>
    <div class="import-stek-dialog">
        <div class="dialog-content">
            <p class="helper-text">
                Upload a stek export ZIP file to import it into your
                organization. This will restore all domains, products,
                processes, and git history.
            </p>
            <div class="field">
                <label class="field-label">Stek Export File</label>
                <FileUpload
                    name="file"
                    mode="advanced"
                    accept=".zip"
                    :maxFileSize="100000000"
                    @select="onFileSelect"
                    @remove="onFileRemove"
                    :showUploadButton="false"
                    :showCancelButton="false"
                    :multiple="false"
                    customUpload
                    class="import-upload-field"
                >
                    <template #header> &nbsp; </template>
                    <template #content="{ files, removeFileCallback }">
                        <div v-if="files.length > 0" class="file-list">
                            <div
                                v-for="file in files"
                                :key="file.name"
                                class="selected-file-item"
                            >
                                <div class="file-info">
                                    <i class="pi pi-file-archive file-icon" />
                                    <span class="file-name">{{
                                        file.name
                                    }}</span>
                                    <span class="file-size"
                                        >({{ formatSize(file.size) }})</span
                                    >
                                </div>
                                <Button
                                    icon="pi pi-times"
                                    severity="danger"
                                    text
                                    rounded
                                    @click="removeFileCallback(0)"
                                    aria-label="Remove file"
                                />
                            </div>
                        </div>
                    </template>
                    <template #empty>
                        <div class="drop-zone-placeholder">
                            <i class="pi pi-cloud-upload upload-icon" />
                            <p class="drop-text">Drag and drop ZIP file here</p>
                            <p class="browse-text">or click to browse</p>
                        </div>
                    </template>
                </FileUpload>
            </div>

            <div class="field">
                <label for="stekName" class="field-label"
                    >New Name (Optional)</label
                >
                <InputText
                    id="stekName"
                    v-model="stekName"
                    placeholder="Leave empty to keep original name"
                    class="name-input"
                    :disabled="isUploading"
                />
                <small class="input-hint">
                    Changing the name helps prevent collisions with existing
                    steks.
                </small>
            </div>
        </div>

        <div class="dialog-footer">
            <Button
                label="Cancel"
                severity="secondary"
                outlined
                @click="handleCancel"
                :disabled="isUploading"
            />
            <Button
                label="Import"
                icon="pi pi-upload"
                @click="handleSubmit"
                :loading="isUploading"
                :disabled="!selectedFile"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import FileUpload from "primevue/fileupload";
import InputText from "primevue/inputtext";
import { useToast } from "primevue/usetoast";
import { Ref, inject, ref } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useSteksStore } from "@/stores/steks-store";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const toast = useToast();
const selectedFile = ref<File | null>(null);
const stekName = ref("");
const isUploading = ref(false);
const { fetchSteks } = useSteksStore();
const { organization } = useOrganizationManager();
const { getToken } = useAuthenticationStore();

const onFileSelect = (event: Event & { files: FileList }) => {
    selectedFile.value = event.files[0];
};

const onFileRemove = () => {
    selectedFile.value = null;
};

const formatSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const handleSubmit = async () => {
    if (!selectedFile.value || !organization.value) return;

    try {
        isUploading.value = true;

        const formData = new FormData();
        formData.append("file", selectedFile.value);
        if (stekName.value) {
            formData.append("name", stekName.value);
        }

        const token = await getToken();
        const baseUrl = import.meta.env.VITE_API_URL;

        const response = await fetch(
            `${baseUrl}/stek/stek_mgt/import_stek/start`,
            {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Organization": organization.value.identity,
                },
                body: formData,
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const detail = errorData.detail?.[0];
            const message =
                typeof detail === "string" ? detail : detail?.message;
            throw new Error(message || `Import failed: ${response.statusText}`);
        }

        toast.add({
            severity: "success",
            summary: "Import Successful",
            detail: "The stek has been imported successfully.",
            life: 3000,
        });

        await fetchSteks();
        dialogRef?.value.close({ success: true });
    } catch (error) {
        console.error("Import failed:", error);
        toast.add({
            severity: "error",
            summary: "Import Failed",
            detail: error instanceof Error ? error.message : "Unknown error",
            life: 5000,
        });
    } finally {
        isUploading.value = false;
    }
};

const handleCancel = () => {
    dialogRef?.value.close();
};
</script>

<style scoped lang="scss">
.import-stek-dialog {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0.25rem;

    .dialog-content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .helper-text {
        font-size: 0.9rem;
        color: var(--text-color-secondary);
        line-height: 1.5;
        margin: 0;
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .field-label {
        font-weight: bold;
        font-size: 0.875rem;
    }

    .name-input {
        width: 100%;
    }

    .input-hint {
        font-size: 0.75rem;
        color: var(--text-color-secondary);
        font-style: italic;
    }

    .import-upload-field {
        width: 100%;
    }

    :deep(.p-fileupload-buttonbar) {
        background: transparent;
        padding: 0;
        border: none;
        margin-bottom: 0.5rem;
    }

    :deep(.p-fileupload-header) {
        display: none;
    }

    :deep(.p-fileupload-content) {
        padding: 2rem;
        border: 2px dashed var(--surface-border);
        border-radius: var(--border-radius);
        background: var(--surface-section);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 150px;
        transition:
            background-color 0.2s,
            border-color 0.2s;
        cursor: pointer;

        &:hover {
            background-color: var(--surface-hover);
            border-color: var(--primary-color);
        }
    }

    .file-list {
        width: 100%;
    }

    .selected-file-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 0.75rem;
        border: 1px solid var(--surface-border);
        border-radius: var(--border-radius);
        background: var(--surface-ground);
        width: 100%;
    }

    .file-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        overflow: hidden;
    }

    .file-icon {
        font-size: 1.25rem;
        color: var(--primary-color);
    }

    .file-name {
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .file-size {
        font-size: 0.75rem;
        color: var(--text-color-secondary);
        white-space: nowrap;
    }

    .drop-zone-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;

        .upload-icon {
            font-size: 2.5rem;
            color: var(--text-color-secondary);
            margin-bottom: 0.5rem;
        }

        .drop-text {
            font-weight: 500;
            margin: 0;
        }

        .browse-text {
            font-size: 0.75rem;
            color: var(--text-color-secondary);
            margin-top: 0.25rem;
        }
    }

    .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding-top: 1rem;
        border-top: 1px solid var(--surface-border);
    }
}
</style>
